import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getBusinessProfile } from '../repository/settingsRepository'

/**
 * How dates are WRITTEN in this business's country — the shape a date picker
 * shows and accepts. Same rule as money: the country, not the UI language and
 * not the browser. A Dominican lender using the console in English still
 * writes 18/08/2026, and an office PC's locale can be anything.
 *
 * Only what a person types and reads in a picker follows this; the API keeps
 * receiving ISO, and dates already shown with the month in letters need no
 * region to be unambiguous.
 */
const BUSINESS_NAME_KEY = 'business.name'

export const useBusinessLocaleStore = defineStore('businessLocale', () => {
  // The Dominican Republic until the business says otherwise: it is where the
  // product is sold, so an unconfigured business reads its own convention.
  const countryCode = ref('DO')
  const resolved = ref(false)
  // Kept across sessions so a phone that starts the day with no signal can
  // still put the lender's name on a provisional receipt.
  const businessName = ref<string | null>(localStorage.getItem(BUSINESS_NAME_KEY))

  /** PrimeVue DatePicker `dateFormat` tokens: `dd/mm/yy` prints 18/08/2026. */
  const dateFormat = computed(() => (countryCode.value === 'US' ? 'mm/dd/yy' : 'dd/mm/yy'))

  const load = async (): Promise<void> => {
    const [error, result] = await getBusinessProfile()
    // A business with no profile yet, or a failed read, keeps the default —
    // never a blank format that would leave every picker unusable.
    if (!error && result?.profile?.countryCode) {
      countryCode.value = result.profile.countryCode
    }
    if (!error && result?.profile) {
      businessName.value = result.profile.businessName || result.profile.displayName || null
      if (businessName.value) {
        localStorage.setItem(BUSINESS_NAME_KEY, businessName.value)
      }
    }
    resolved.value = true
  }

  return { countryCode, dateFormat, businessName, resolved, load }
})
